// src/components/MobilePasswordReset.js
// Intercepts Firebase password reset redirect and immediately redirects to mobile app
import React, { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import SEO from './SEO';
import { Loader2 } from 'lucide-react';
import Logo from './Logo';
import Footer from './Footer';

function MobilePasswordReset() {
  const [searchParams] = useSearchParams();
  const oobCode = searchParams.get('oobCode');
  const mode = searchParams.get('mode');

  useEffect(() => {
    // Log for debugging
    console.log('[MobilePasswordReset] Intercepting Firebase redirect:', {
      mode,
      oobCode: oobCode ? `${oobCode.substring(0, 20)}...` : null,
      fullUrl: window.location.href,
      search: window.location.search
    });

    // Validate we have required parameters
    if (mode !== 'resetPassword') {
      console.error('[MobilePasswordReset] Invalid mode:', mode);
      // Fallback to web reset page
      window.location.href = `/auth?mode=${mode || 'resetPassword'}&oobCode=${oobCode || ''}`;
      return;
    }

    if (!oobCode) {
      console.error('[MobilePasswordReset] Missing oobCode');
      // Fallback to web reset page
      window.location.href = '/auth?mode=resetPassword';
      return;
    }

    // Extract oobCode and redirect to mobile app immediately
    // Mobile app will handle the password reset using this oobCode
    const mobileDeepLink = `epiphoricmobile://auth/reset?oobCode=${encodeURIComponent(oobCode)}&mode=resetPassword`;
    
    console.log('[MobilePasswordReset] Redirecting to mobile app:', mobileDeepLink);
    
    // Redirect to mobile app immediately
    window.location.href = mobileDeepLink;

    // Fallback: If app doesn't open within 2 seconds, redirect to web reset page
    setTimeout(() => {
      // Only redirect if still on this page (app didn't open)
      if (window.location.pathname === '/auth/mobile-reset') {
        console.warn('[MobilePasswordReset] Mobile app did not open, redirecting to web reset page');
        window.location.href = `/auth?mode=resetPassword&oobCode=${encodeURIComponent(oobCode)}`;
      }
    }, 2000);
  }, [mode, oobCode, searchParams]);

  return (
    <>
      <SEO 
        title="Redirecting to Mobile App - Epiphoric"
        description="Opening password reset in mobile app"
      />
      <div className="flex flex-col min-h-screen relative">
        <div className="flex-1 flex items-center justify-center p-8">
          <div className="text-center max-w-md w-full">
            <Logo className="mx-auto mb-8" />
            <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              Opening Mobile App...
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              Please complete your password reset in the Epiphoric mobile app.
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-500 mt-4">
              If the app doesn't open automatically,{' '}
              <a href={`/auth?mode=resetPassword&oobCode=${oobCode || ''}`} className="text-primary hover:underline">
                continue on web
              </a>
            </p>
          </div>
        </div>
        <Footer variant="ghost" />
      </div>
    </>
  );
}

export default MobilePasswordReset;
